import { parseCSVFile, parseXLSXFile } from '@utils/file';
import {
  isInvalidEmail,
  isInvalidName,
  isInvalidPhoneNumber
} from '@utils/validations';

import {
  LeadAssignee,
  LeadCategory,
  LeadPriority,
  LeadSource,
  LeadStatus,
  LeadTag
} from '../types/common';
import {
  LeadDataMappingLists,
  LeadUploadRow,
  LeadValidationLists
} from '../types/leadBulkCreate';
import { CreateLeadRequest } from '../types/request';

export type PreparedUploadData = {
  headers: string[];
  rowsWithId: (LeadUploadRow & { __rowId: string })[];
  rowErrorsById: Record<string, string[]>;
};

const isEmpty = (val?: string | null) => !val || `${val}`.trim() === '';

const isInvalidValue = (value?: string, list?: string[]) => {
  if (!value) return false;
  return !list?.includes(value.trim());
};

export const validateRow = (
  row: LeadUploadRow,
  lists: LeadValidationLists
): string[] => {
  const errs: string[] = [];
  if (isEmpty(row.Name)) errs.push(`Name: Required field`);
  if (row.Name && isInvalidName(row.Name.toString()))
    errs.push(`Name: Invalid value`);
  if (isEmpty(row['Email ID'])) errs.push(`Email ID: Required field`);
  if (row['Email ID'] && isInvalidEmail(row['Email ID'].toString()))
    errs.push(`Email ID: Invalid value`);
  if (isEmpty(row['Country Code'])) {
    errs.push(`Country Code: Required field`);
  } else if (row['Country Code'] && row['Country Code'].startsWith('+')) {
    errs.push(`Country Code: Invalid value`);
  }
  if (isEmpty(row['Mobile Number'])) errs.push(`Mobile Number: Required field`);
  if (
    row['Mobile Number'] &&
    isInvalidPhoneNumber(row['Mobile Number'].toString())
  )
    errs.push(`Mobile Number: Invalid value`);
  if (
    row['Secondary Mobile Number'] &&
    isInvalidPhoneNumber(row['Secondary Mobile Number'].toString())
  )
    errs.push(`Secondary Mobile Number: Invalid value`);
  if (row['Secondary Mobile Number'] && isEmpty(row['Secondary Country Code']))
    errs.push(`Secondary Country Code: Invalid value`);
  if (isEmpty(row.Priority)) errs.push(`Priority: Required field`);
  if (isEmpty(row.Source)) errs.push(`Source: Required field`);
  if (isEmpty(row.Status)) errs.push(`Status: Required field`);
  if (isInvalidValue(row.Status, lists.statuses))
    errs.push(`Status: Invalid value`);
  if (isInvalidValue(row.Source, lists.sources))
    errs.push(`Source: Invalid value`);
  if (isInvalidValue(row.Category, lists.categories))
    errs.push(`Category: Invalid value`);
  if (isInvalidValue(row.Priority, lists.priorities))
    errs.push(`Priority: Invalid value`);
  if (isInvalidValue(row.Assignee, lists.assignees))
    errs.push(`Assignee: Invalid value`);
  if (isInvalidValue(row.City, lists.cities)) errs.push(`City: Invalid value`);
  if (
    Array.isArray(row.Tags)
      ? row.Tags.some((tag) => isInvalidValue(tag, lists.tags))
      : isInvalidValue(row.Tags, lists.tags)
  ) {
    errs.push(`Tags: Invalid value`);
  }

  if (row['Deal Value'] !== undefined && row['Deal Value'] !== null) {
    const n = Number(row['Deal Value']);
    if (Number.isNaN(n) || n < 0)
      errs.push(`Deal Value: value must be a positive number`);
  }
  return errs;
};

const normalizeTags = (
  rowTags: string | string[],
  tagsList: LeadTag[]
): string[] | undefined => {
  if (!rowTags) return undefined;
  if (Array.isArray(rowTags)) return rowTags.filter(Boolean).map(String);
  const normalizedTags = rowTags
    .split(',')
    .map((t: string) => t.trim())
    .filter(Boolean);
  return tagsList
    ?.filter((t: LeadTag) => normalizedTags.includes(t.name))
    .map((t: LeadTag) => t.id);
};

export const toCreatePayload = (
  row: LeadUploadRow,
  leadDataMappingLists: LeadDataMappingLists
): CreateLeadRequest => {
  const {
    statusList,
    sourceList,
    tagsList,
    categoryList,
    priorityList,
    assigneeList
  } = leadDataMappingLists;
  const statusId = statusList?.find(
    (s: LeadStatus) => s.value === row.Status
  )?.id;
  const sourceId = sourceList?.find(
    (s: LeadSource) => s.label === row.Source
  )?.id;
  const assigneeId = assigneeList?.find(
    (a: LeadAssignee) => a.name === row.Assignee
  )?.user_id;
  const categoryId = categoryList?.find(
    (c: LeadCategory) => c.value === row.Category
  )?.id;
  const priorityId = priorityList?.find(
    (p: LeadPriority) => p.label === row.Priority
  )?.id;
  const tagsIds = normalizeTags(row.Tags as string | string[], tagsList);

  const dealValueNum =
    row['Deal Value'] === undefined ||
    row['Deal Value'] === null ||
    `${row['Deal Value']}`.trim() === ''
      ? undefined
      : Number(row['Deal Value']) * 100;

  const state = row?.State;
  const city = row?.City;
  const country = row?.Country;
  const pincode = row?.PinCode;

  const isSecondaryMobileNumberEmpty = isEmpty(row['Secondary Mobile Number']);

  return {
    name: row.Name || '',
    email_id: isEmpty(String(row['Email ID'])) ? undefined : row['Email ID'],
    country_code: String(row['Country Code']) || '',
    mobile_number: String(row['Mobile Number']) || '',
    secondary_mobile_number: isSecondaryMobileNumberEmpty
      ? undefined
      : String(row['Secondary Mobile Number']),
    secondary_country_code: isSecondaryMobileNumberEmpty
      ? undefined
      : String(row['Secondary Country Code']),
    country: country || undefined,
    city: city || undefined,
    state: state || undefined,
    pincode: pincode || undefined,
    category_id: isEmpty(categoryId) ? null : categoryId,
    source: isEmpty(sourceId) ? null : sourceId,
    assignee_id: isEmpty(assigneeId) ? null : assigneeId,
    status: isEmpty(statusId) ? null : statusId,
    deal_value: Number.isNaN(dealValueNum as number)
      ? undefined
      : (dealValueNum as number),
    priority: isEmpty(priorityId) ? null : priorityId,
    description: isEmpty(String(row.Description)) ? undefined : row.Description,
    tags: tagsIds
  } as CreateLeadRequest;
};

export const prepareLeadUpload = async (
  selectedFile: File,
  lists: LeadValidationLists
): Promise<PreparedUploadData> => {
  let parsedData: any;
  if (selectedFile.type === 'text/csv') {
    parsedData = await parseCSVFile(selectedFile);
  } else {
    parsedData = await parseXLSXFile(selectedFile);
  }

  const parsedRows = (parsedData?.data || []) as unknown as LeadUploadRow[];

  const rowsWithId = parsedRows.map((r, idx) => ({
    ...r,
    __rowId: `row-${idx}`
  }));

  const inferredHeaders: string[] =
    parsedData?.meta?.fields?.filter((h: string) => h && !h.startsWith('_')) ||
    (rowsWithId.length
      ? Object.keys(rowsWithId[0] as Record<string, unknown>).filter(
          (h) => h !== '__rowId'
        )
      : []);

  const perRowErrs: Record<string, string[]> = {};
  rowsWithId.forEach((r) => {
    const errs = validateRow(r, lists);
    // eslint-disable-next-line no-underscore-dangle
    perRowErrs[r.__rowId as string] = errs;
  });

  return { headers: inferredHeaders, rowsWithId, rowErrorsById: perRowErrs };
};
